import React, { useState, useEffect } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import './security-info-modal.scss';

const formatWithCommas = (value) => {
  let [integerPart, decimalPart] = value.split(".");
  if (integerPart) {
    integerPart = parseInt(integerPart, 10).toLocaleString("en-US");
  }
  if (value.includes(".")) {
    decimalPart = (decimalPart || "").slice(0, 2);
    return decimalPart.length > 0 ? `${integerPart}.${decimalPart}` : `${integerPart}.`;
  }
  return integerPart;
};

const SecurityInfoModal = ({ show, onHide, onConfirm }) => {
  const [formData, setFormData] = useState({
    clientName: "",
    bpId: "",
    esipReferenceNumber: "",
    amount: "",
    status: "Not Submitted"
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (show) {
      setFormData({
        clientName: "",
        bpId: "",
        esipReferenceNumber: "",
        amount: "",
        status: "Not Submitted"
      });
      setErrors({});
    }
  }, [show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "clientName") {
      newValue = newValue.slice(0, 250);
    }

    if (name === "bpId") {
      // Allow only alphanumeric characters and limit to 11 characters.
      newValue = newValue.replace(/[^a-zA-Z0-9]/g, '').slice(0, 11);
    }

    if (name === "esipReferenceNumber") {
      // Allow only alphanumeric characters and '-' as a special character, limit to 100 characters.
      newValue = newValue.replace(/[^a-zA-Z0-9-]/g, '').slice(0, 100);
    }

    if (name === "amount") {
      let rawValue = newValue.replace(/,/g, '').slice(0, 100);
      // Allow empty string so deletion works correctly.
      if (rawValue === "") {
        newValue = "";
      } else if (/^\d+(\.?\d{0,2})?$/.test(rawValue)) {
        newValue = formatWithCommas(rawValue);
      } else {
        newValue = formData.amount;
      }
    }

    setFormData((prev) => ({ ...prev, [name]: newValue }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleConfirm = () => {
    let newErrors = {};

    if (!formData.clientName.trim()) {
      newErrors.clientName = "Client's Name is required";
    }
    if (!formData.bpId) {
      newErrors.bpId = "bpId is required";
    } else if (formData.bpId.length !== 11) {
      newErrors.bpId = "bpId must be exactly 11 characters";
    }
    if (!formData.esipReferenceNumber) {
      newErrors.esipReferenceNumber = "e-SIP Reference Number is required";
    } else if (!/^[a-zA-Z0-9-]*$/.test(formData.esipReferenceNumber)) {
      newErrors.esipReferenceNumber = "e-SIP Reference Number must be alphanumeric and can only include '-' as a special character";
    }
    if (!formData.amount) {
      newErrors.amount = "Amount is required";
    } else {
      const rawAmount = formData.amount.replace(/,/g, '');
      if (!/^\d+(\.?\d{0,2})?$/.test(rawAmount)) {
        newErrors.amount = "Amount must be a number with up to two decimal places";
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (onConfirm) {
      onConfirm(formData);
    }
  };

  return (
    <Modal show={show} onHide={onHide} dialogClassName="security-info-modal">
      <Modal.Header closeButton>
        <Modal.Title>Enter Security Info</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group controlId="modalClientName">
            <Form.Label>Client's Name <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="clientName"
              value={formData.clientName}
              onChange={handleInputChange}
              isInvalid={!!errors.clientName}
            />
            <Form.Control.Feedback type="invalid">
              {errors.clientName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="modalbpId">
            <Form.Label>bpId <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="bpId"
              value={formData.bpId}
              onChange={handleInputChange}
              isInvalid={!!errors.bpId}
            />
            <Form.Control.Feedback type="invalid">
              {errors.bpId}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="modalEsipReferenceNumber">
            <Form.Label>e-SIP Reference Number <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="esipReferenceNumber"
              value={formData.esipReferenceNumber}
              onChange={handleInputChange}
              isInvalid={!!errors.esipReferenceNumber}
            />
            <Form.Control.Feedback type="invalid">
              {errors.esipReferenceNumber}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group controlId="modalAmount">
            <Form.Label>Amount <span className="text-danger">*</span></Form.Label>
            <Form.Control
              type="text"
              name="amount"
              value={formData.amount}
              onChange={handleInputChange}
              isInvalid={!!errors.amount}
            />
            <Form.Control.Feedback type="invalid">
              {errors.amount}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleConfirm}>
          Confirm
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SecurityInfoModal;
